/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { WiDayCloudy } from "react-icons/wi";
import { HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Action, Column } from "../../ui";
import { useClimateContext } from "../../context/ClimateContext";
import { pushOneClimate } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";
import { useNavigate } from "react-router-dom";

type Climate = {
  climate_id: number | string;
  name: string;
  [key: string]: unknown;
};

const addRoute = "/climate/add";
const editRoute = "/climate/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const {
    climate,
    count,
    fetchClimates,
    deleteClimate,
    updateClimateState,
  } = useClimateContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!climate) fetchClimates(page, pageSize);
  }, [climate]);

  const rows = useMemo<Climate[] | null>(() => {
    if (!Array.isArray(climate)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return climate as Climate[];
    return (climate as Climate[]).filter((c) =>
      c.name?.toString().toLowerCase().includes(q),
    );
  }, [climate, query]);

  const columns: Column<Climate>[] = [
    {
      header: "Name",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-inset ring-brand/15"
          >
            <WiDayCloudy className="h-4 w-4" />
          </span>
          <span className="font-medium text-gray-900">{row.name}</span>
        </div>
      ),
    },
  ];

  const handleDelete = async (row: Climate) => {
    const response: any = await deleteClimate(row.climate_id);
    if (response?.data?.status === 200 && Array.isArray(climate)) {
      updateClimateState(
        (climate as Climate[]).filter((c) => c.climate_id !== row.climate_id),
      );
    }
  };

  const rowActions = (row: Climate): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneClimate(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.name}?`,
        description:
          "This climate will be removed from every country and level that references it. This action cannot be undone.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<Climate>
      title="Climate"
      subtitle="Browse, add and manage climate types."
      icon={<WiDayCloudy className="h-5 w-5" />}
      actions={[
        {
          label: "Add climate",
          variant: "primary",
          href: addRoute,
        },
      ]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.climate_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search climates…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchClimates(p, ps);
        },
      }}
      empty="No climates yet. Add your first one to populate the list."
    />
  );
};

export default Overview;
